import { SOW, CustomerInfo } from '../models/sow';
import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const sowService = {
  async createSOW(address: string): Promise<SOW> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      console.log('Headers being sent:', headers);
      
      const response = await fetch(`${API_BASE_URL}/statementofwork`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          address,
        }),
      });

      const data = await handleResponse(response);
      return data.sow;
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async getSOW(sowId: string): Promise<SOW> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async updateSOW(sowId: string, updateData: Partial<SOW>): Promise<SOW> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await handleResponse(response);
      return data.sow;
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async getSOWRegistry(): Promise<{ sowRegistry: Array<{ id: string; address: string; status: string; date: string }> }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/statementofwork`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async uploadImageForAnalysis(sowId: string, file: File): Promise<string> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/workanalyzer/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const uploadResult = await response.json();
      return uploadResult.id;
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  },

  async analyzeSOWImage(sowId: string, imageId: string, projectType: string, questionnaireContext?: { type: string; questionId?: string; questionnaireId?: string }): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/image/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          imageId: imageId,
          projectType: projectType,
          questionnaireContext: questionnaireContext
        })
      });

      if (!response.ok) {
        throw new Error('Failed to analyze image');
      }

      return await response.json();
    } catch (error) {
      console.error('Image analysis error:', error);
      throw error;
    }
  },

  async getSOWImages(sowId: string): Promise<{ images: any[] }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/images`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get SOW images');
      }

      return await response.json();
    } catch (error) {
      console.error('Get SOW images error:', error);
      throw error;
    }
  },

  async assessImageQuality(sowId: string, imageId: string, questionId: string, questionText: string, attemptNumber: number = 1): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/image/${imageId}/assess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          questionId,
          questionText,
          attemptNumber
        })
      });

      if (!response.ok) {
        throw new Error('Failed to assess image quality');
      }

      const result = await response.json();
      return result.assessment;
    } catch (error) {
      console.error('Image quality assessment error:', error);
      throw error;
    }
  },

  async getQuestionnaireQuestions(sowId: string, questionnaireId: string): Promise<{ 
    questions: any[]; 
    filteredStats: { originalCount: number; filteredCount: number; skippedCount: number }; 
    questionnaireId: string; 
    questionnaireName: string 
  }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/${questionnaireId}/questions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get questionnaire questions');
      }

      return await response.json();
    } catch (error) {
      console.error('Get questionnaire questions error:', error);
      throw error;
    }
  },

  async generateProjectEstimate(sowId: string): Promise<{
    status: string;
    projectPlan: any;
    summary: {
      totalWorkItems: number;
      completedWorkItems: number;
      estimatedCostRange: { low: number; high: number };
      confidence: number;
    };
  }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate project estimate');
      }

      return await response.json();
    } catch (error) {
      console.error('Generate project estimate error:', error);
      throw error;
    }
  },
};